import * as React from "react";
import { CheckIcon } from "@radix-ui/react-icons";
import { cn, render, Renderable } from "@/lib/utils";
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/custom/button.tsx";

interface StepperProps {
  steps: string[];
  currentStep: number;
}

const Stepper: React.FC<StepperProps> = ({ steps, currentStep }) => {
  return (
    <div className="w-full flex justify-center px-4 sm:px-10 py-10">
      <div className={cn("relative", steps.length > 3 ? "w-[85%]" : "w-[60%]")}>
        {/* Connecting line container */}
        <div className="absolute top-1/2 left-4 right-4 sm:left-10 sm:right-10 flex items-center">
          <div className="flex-1 h-0.5 bg-muted relative">
            {/* Progress line overlay */}
            <div
              className="absolute left-0 top-0 h-full bg-azure transition-all duration-300"
              style={{
                width: `${currentStep > 0 ? (currentStep / (steps.length - 1)) * 100 : 0}%`,
              }}
            />
          </div>
        </div>

        {/* Steps container */}
        <div className="relative flex justify-between items-center">
          {steps.map((label, index) => {
            const isCompleted = index < currentStep;
            const isActive = index === currentStep;

            return (
              <div key={label} className="flex flex-col items-center relative">
                {/* Step circle */}
                <div
                  className={cn(
                    "flex items-center justify-center w-6 h-6 rounded-full text-sm font-medium relative z-10 bg-background",
                    isCompleted
                      ? "bg-azure text-azure-foreground"
                      : isActive
                        ? "border-2 border-azure text-azure bg-background"
                        : "bg-muted text-muted-foreground border-2 border-muted",
                  )}
                >
                  {isCompleted ? <CheckIcon className="w-3 h-3" /> : index + 1}
                </div>

                {/* Step label - positioned absolutely to not affect spacing */}
                <div className="absolute top-8 left-1/2 transform -translate-x-1/2 w-max max-w-24">
                  <p
                    className={cn(
                      "text-xs text-center leading-tight",
                      isActive
                        ? "text-azure font-medium"
                        : "text-muted-foreground",
                    )}
                  >
                    {label}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export type DialogPage = {
  title: string;
  content: Renderable;
  footer?: Renderable;
  fields?: string[];
  // Promise<string | boolean> rather than Promise<string> | Promise<boolean>:
  // checkBlocked already discriminates on the resolved value, and the narrower
  // form rejects any async blocker that can return either.
  blocker?: () => boolean | string | Promise<boolean | string>;
  hideNext?: boolean;
  hideBack?: boolean;
  disableNext?: boolean;
  scroll?: boolean;
};

interface MultiStepDialogProps extends React.ComponentProps<typeof Dialog> {
  title?: string;
  pages: DialogPage[];
  currentStep?: number;
  onNext?: (...args: any[]) => void;
  onBack?: (...args: any[]) => void;
  onSubmit: (data?: any) => void | Promise<void>;
  finishButtonText?: string;
  backButtonText?: string;
  className?: string;
}

export const MultiStepDialogContent: React.FC<MultiStepDialogProps> = ({
  title,
  pages,
  currentStep,
  onNext,
  onBack,
  onSubmit,
  finishButtonText = "Submit",
  backButtonText = "Back",
  className,
}) => {
  const [blocked, setBlocked] = React.useState<{
    blocked: boolean;
    reason?: string;
  }>({ blocked: false });
  const [internalStep, setInternalStep] = React.useState(0);
  const isControlled = currentStep !== undefined;
  const step = isControlled ? currentStep : internalStep;

  const totalSteps = pages.length;
  const isLastStep = step === totalSteps - 1;

  const handleInternalNext = (..._args: any[]) => {
    setInternalStep((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const handleInternalBack = (..._args: any[]) => {
    setInternalStep((prev) => Math.max(prev - 1, 0));
  };

  const checkBlocked = React.useCallback(
    async (options?: { ref?: { current: boolean } }) => {
      const page = pages?.[step];
      const blocker = page?.blocker;
      if (blocker) {
        const result = await blocker();
        if (typeof result === "string" && result) {
          if (options?.ref?.current !== false) {
            setBlocked({ blocked: true, reason: result as string });
          }
          return true;
        }
        if (result === false) {
          if (options?.ref?.current !== false) {
            setBlocked({ blocked: true });
          }
          return true;
        }
        if (options?.ref?.current !== false) {
          setBlocked({ blocked: false });
        }
        return false;
      }
    },
    [step, pages],
  );

  // React.useEffect(() => {
  //   const ref = { current: true };
  //   checkBlocked({ ref });
  //   return () => {
  //     ref.current = false;
  //   };
  // }, [checkBlocked]);

  const page = pages[step];
  if (page === undefined) return undefined;

  return (
    <div
      className={cn(
        "min-w-96 w-full p-0 flex flex-col flex-1 min-h-0",
        className,
      )}
    >
      {title && (
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
      )}

      {/* Stepper Navigation */}
      <div className="flex-shrink-0">
        <Stepper steps={pages.map((page) => page.title)} currentStep={step} />
      </div>

      {/* Page Content */}
      {page?.scroll === false ? (
        render(page.content)
      ) : (
        /* `dialog-scrollable-content` sets overflow-y, which forces computed
           overflow-x to `auto` — so this box clips horizontally. At md+ its
           padding was 0, leaving w-full fields flush against that clip edge
           with no room for their non-inset focus ring. The negative margin
           offsets the padding, so this buys paint room without shifting
           content. See ID-6866. */
        <div className="flex-1 min-h-0 px-4 md:-mx-2 md:px-2 dialog-scrollable-content">
          {render(page.content)}
        </div>
      )}

      <DialogFooter className="w-full !pt-6 flex-shrink-0">
        <div className="flex justify-between w-full">
          <div className="flex items-center gap-4 ml-auto space-x-2">
            {blocked?.blocked && blocked?.reason && (
              <span className="text-destructive">{blocked.reason}</span>
            )}
            <div className="flex items-center gap-2">
              {step > 0 && !page.hideBack && (
                <Button
                  variant="outline"
                  onClick={onBack ?? handleInternalBack}
                  type="button"
                >
                  {backButtonText}
                </Button>
              )}
              {!page.hideNext && (
                <Button
                  variant={"azure"}
                  disabled={page.disableNext}
                  onClick={async (...args) => {
                    if (await checkBlocked()) {
                      return;
                    }
                    return isLastStep
                      ? onSubmit(...args)
                      : (onNext ?? handleInternalNext)(...args);
                  }}
                  type="button"
                >
                  {isLastStep ? finishButtonText : "Next"}
                </Button>
              )}
              {page.footer && render(page.footer)}
            </div>
          </div>
        </div>
      </DialogFooter>
    </div>
  );
};
